import { useEffect } from "react";
import { nextDemoId, preloadDemo } from "@/demos/registry";
import type { DemoId } from "@/demos/types";
import { EighthWallRuntime } from "@/runtime/EighthWallRuntime";
import type { RuntimePhase } from "@/runtime/types";

interface DemoPreloadingOptions {
  activeDemoId: DemoId;
  started: boolean;
  phase: RuntimePhase;
  eighthWallRuntime: EighthWallRuntime;
  getCapabilities: () => Promise<{ preferredMode: string }>;
}

export function useDemoPreloading({ activeDemoId, started, phase, eighthWallRuntime, getCapabilities }: DemoPreloadingOptions): void {
  useEffect(() => {
    if (started) return;
    const schedule = window.requestIdleCallback ?? ((callback: IdleRequestCallback) => window.setTimeout(callback, 500));
    const cancel = window.cancelIdleCallback ?? window.clearTimeout;
    const task = schedule(() => {
      void getCapabilities().then((capability) => {
        if (capability.preferredMode === "eighth-wall") return eighthWallRuntime.preload();
        return undefined;
      }).catch(() => undefined);
    }, { timeout: 1_500 });
    return () => cancel(task);
  }, [eighthWallRuntime, getCapabilities, started]);

  useEffect(() => {
    if (!started || phase !== "placed") return;
    const nextId = nextDemoId(activeDemoId);
    const schedule = window.requestIdleCallback ?? ((callback: IdleRequestCallback) => window.setTimeout(callback, 250));
    const cancel = window.cancelIdleCallback ?? window.clearTimeout;
    const task = schedule(() => {
      void preloadDemo(nextId).catch((cause: unknown) => {
        console.warn(`Background preload for ${nextId} failed; navigation will retry it.`, cause);
      });
    });
    return () => cancel(task);
  }, [activeDemoId, phase, started]);
}
